/**
 * Turn a raw Supabase / Postgres error into a sentence that's safe to show a
 * user. PostgrestError (and AuthError / StorageError) all extend Error, so the
 * common `err instanceof Error ? err.message : "…"` guard leaks raw SQL text
 * like `violates foreign key constraint "time_logs_activation_id_fkey"` into a
 * toast. This maps the SQLSTATE on `.code` to plain language instead.
 *
 * `fallback` preserves each call site's existing context string and is returned
 * for any code we don't map — so switching a call site over is strictly an
 * improvement and never regresses. `overrides` lets a site sharpen a specific
 * SQLSTATE (especially 23503) with app-specific copy.
 */
type SupabaseErrorish = {
  message?: string;
  code?: string; // SQLSTATE for DB errors, e.g. "23503"
  details?: string;
  hint?: string;
};

function asSupabaseError(err: unknown): SupabaseErrorish | null {
  if (err && typeof err === "object") return err as SupabaseErrorish;
  return null;
}

/**
 * Geolocation failures. getCurrentPosition() rejects with `{ code, message }`
 * mirroring GeolocationPositionError: 1 = permission denied, 2 = position
 * unavailable, 3 = timeout, plus our own 0 = API missing. We throw a
 * LocationError carrying user-facing copy so both check-in and check-out show
 * a specific message instead of the generic fallback.
 */
export const LOCATION_PERMISSION_DENIED_MESSAGE =
  "Location is off for Staff Hub. Turn it on in your phone settings, then try again.";
export const LOCATION_UNAVAILABLE_MESSAGE =
  "Couldn't get your location. Move somewhere with a clearer signal and try again.";

export class LocationError extends Error {
  readonly code: number;
  constructor(code: number, message: string) {
    super(message);
    this.name = "LocationError";
    this.code = code;
  }
}

function isGeolocationErrorish(err: unknown): err is { code: number; message?: string } {
  return (
    !!err &&
    typeof err === "object" &&
    typeof (err as { code?: unknown }).code === "number" &&
    !(err instanceof Error)
  );
}

/** Map a getCurrentPosition() rejection to a LocationError with specific copy. */
export function toLocationError(err: unknown): LocationError {
  if (err instanceof LocationError) return err;
  const code = isGeolocationErrorish(err) ? err.code : 2;
  // 1 = PERMISSION_DENIED; anything else (0 missing API, 2 unavailable, 3 timeout)
  return new LocationError(
    code,
    code === 1 ? LOCATION_PERMISSION_DENIED_MESSAGE : LOCATION_UNAVAILABLE_MESSAGE,
  );
}

export function humanizeSupabaseError(
  err: unknown,
  fallback: string,
  overrides?: Partial<Record<string, string>>,
): string {
  // Location failures carry their own user-facing copy — never the fallback.
  if (err instanceof LocationError) return err.message;

  const code = asSupabaseError(err)?.code;
  if (code && overrides?.[code]) return overrides[code]!;

  switch (code) {
    case "23503": // foreign_key_violation
      return "This record is still in use elsewhere and can't be removed yet.";
    case "23505": // unique_violation
      return "That already exists — please use a different value.";
    case "23502": // not_null_violation
      return "A required field is missing. Please fill everything in and try again.";
    case "23514": // check_violation
      return "Those values aren't valid — please double-check them.";
    case "42501": // insufficient_privilege (RLS)
      return "You don't have permission to do that.";
    default:
      return fallback;
  }
}
